/**
 * Media Query Hook for Responsive Design
 *
 * `useIsMobile` is the one breakpoint the app switches layouts on.
 * SSR-safe.
 *
 * @example
 * ```tsx
 * import { useIsMobile } from '@shared/hooks/useMediaQuery';
 *
 * function MyComponent() {
 *   const isMobile = useIsMobile();
 *
 *   if (isMobile) {
 *     return <MobileView />;
 *   }
 *   return <DesktopView />;
 * }
 * ```
 */

import { useState, useEffect } from 'react';

/** Viewports narrower than this (phones in portrait) get the mobile layout. */
const MOBILE_BREAKPOINT_PX = 768;

/**
 * Hook to check if a media query matches
 *
 * @param query - CSS media query string (e.g., "(max-width: 768px)")
 * @returns true if the media query matches, false otherwise
 *
 * @example
 * ```tsx
 * const isDark = useMediaQuery('(prefers-color-scheme: dark)');
 * const isSmall = useMediaQuery('(max-width: 600px)');
 * ```
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    // SSR-safe: default to false on server
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    // Skip if window is not available (SSR)
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(query);

    // Update state when query match changes
    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Set initial value (in case it changed between render and effect)
    setMatches(mediaQuery.matches);

    // Listen for changes
    // Note: Safari < 14 uses addListener/removeListener instead of addEventListener
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, [query]);

  return matches;
}

/**
 * Hook to detect mobile devices (< 768px)
 *
 * @returns true if viewport width is less than 768px
 *
 * @example
 * ```tsx
 * function HoldingsView() {
 *   const isMobile = useIsMobile();
 *   return isMobile ? <CardView /> : <TableView />;
 * }
 * ```
 */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
}
